import React, { useState, useEffect } from 'react';
import './AdminCommissions.css';

function CommissionConfigEditor({
  config,
  onSave,
  onCancel,
  saving,
  stablecoinOptions,
  creditTypeOptions
}) {
  const [editedConfig, setEditedConfig] = useState(() => ({ ...config }));

  useEffect(() => {
    setEditedConfig({ ...config });
  }, [config]);

  const handleSave = () => {
    onSave(config.methodology, {
      commission_percent: parseFloat(editedConfig.commission_percent),
      commission_credit_type: editedConfig.commission_credit_type,
      custom_credit_percent: editedConfig.custom_credit_percent ? parseFloat(editedConfig.custom_credit_percent) : null,
      supported_stablecoins: editedConfig.supported_stablecoins,
      enabled: editedConfig.enabled
    });
  };

  const toggleStablecoin = (symbol) => {
    const current = editedConfig.supported_stablecoins || [];
    const updated = current.includes(symbol)
      ? current.filter(s => s !== symbol)
      : [...current, symbol];

    setEditedConfig({ ...editedConfig, supported_stablecoins: updated });
  };

  return (
    <div className="config-editor">
      <div className="config-row">
        <label>Commission %</label>
        <input
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={editedConfig.commission_percent}
          onChange={(e) => setEditedConfig({ ...editedConfig, commission_percent: e.target.value })}
          className="config-input"
        />
      </div>

      <div className="config-row">
        <label>Commission Credit Type</label>
        <select
          value={editedConfig.commission_credit_type}
          onChange={(e) => setEditedConfig({ ...editedConfig, commission_credit_type: e.target.value })}
          className="config-select"
        >
          {creditTypeOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {editedConfig.commission_credit_type === 'custom' && (
        <div className="config-row">
          <label>Custom Credit %</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max={editedConfig.commission_percent}
            value={editedConfig.custom_credit_percent || 0}
            onChange={(e) => setEditedConfig({ ...editedConfig, custom_credit_percent: e.target.value })}
            className="config-input"
          />
        </div>
      )}

      <div className="config-row">
        <label>Supported Stablecoins</label>
        <div className="stablecoin-checkboxes">
          {stablecoinOptions.map(coin => (
            <label key={coin.symbol} className="checkbox-label">
              <input
                type="checkbox"
                checked={(editedConfig.supported_stablecoins || []).includes(coin.symbol)}
                onChange={() => toggleStablecoin(coin.symbol)}
              />
              {coin.symbol} ({coin.name})
            </label>
          ))}
        </div>
      </div>

      <div className="config-row">
        <label>Enabled</label>
        <input
          type="checkbox"
          checked={editedConfig.enabled === 1}
          onChange={(e) => setEditedConfig({ ...editedConfig, enabled: e.target.checked ? 1 : 0 })}
        />
      </div>

      <div className="config-actions">
        <button type="button" onClick={handleSave} disabled={saving} className="btn-save">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button type="button" onClick={onCancel} className="btn-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}

const AdminCommissions = ({ user }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingMethodology, setEditingMethodology] = useState(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [usersWithCredits, setUsersWithCredits] = useState([]);
  const [selectedTab, setSelectedTab] = useState('configs'); // configs, withdrawals, summary, users

  // Available stablecoins
  const STABLECOIN_OPTIONS = [
    { symbol: 'USDC', name: 'USD Coin' },
    { symbol: 'USDT', name: 'Tether USD' },
    { symbol: 'PYUSD', name: 'PayPal USD' },
    { symbol: 'USDS', name: 'Sky Dollar' },
    { symbol: 'EURC', name: 'Euro Coin' }
  ];

  const CREDIT_TYPE_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'buy', label: 'Buy Only' },
    { value: 'sell', label: 'Sell Only' },
    { value: 'lesser', label: 'Lesser of Buy/Sell' },
    { value: 'custom', label: 'Custom %' }
  ];

  const METHODOLOGY_LABELS = {
    'meme_coins': 'Meme Coins',
    'correlation': 'Correlation Trades',
    'scalping': 'Scalping',
    'turtle': 'Turtle'
  };

  // Load data on mount
  useEffect(() => {
    if (user?.is_admin) {
      loadAllData();
    }
  }, [user]);

  // Load all admin data
  const loadAllData = async () => {
    await Promise.all([
      loadConfigs(),
      loadPendingWithdrawals(),
      loadCommissionSummary(),
      loadUsersWithCredits()
    ]);
  };

  // Load commission configs
  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/meme-coins/commission-configs?adminUserId=${user.id}`
      );
      const data = await response.json();

      if (data.success) {
        setConfigs(data.configs);
      } else {
        alert('Failed to load commission configs');
      }
    } catch (error) {
      console.error('Error loading configs:', error);
      alert('Error loading commission configs');
    } finally {
      setLoading(false);
    }
  };

  // Load pending withdrawals
  const loadPendingWithdrawals = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/meme-coins/pending-withdrawals?adminUserId=${user.id}&status=pending`
      );
      const data = await response.json();

      if (data.success) {
        setPendingWithdrawals(data.withdrawals);
      }
    } catch (error) {
      console.error('Error loading withdrawals:', error);
    }
  };

  // Load commission summary
  const loadCommissionSummary = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/meme-coins/commission-summary?adminUserId=${user.id}`
      );
      const data = await response.json();

      if (data.success) {
        setCommissionSummary(data.summary);
      }
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  // Load users with credits
  const loadUsersWithCredits = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/meme-coins/users-with-credits?adminUserId=${user.id}&minBalance=0`
      );
      const data = await response.json();

      if (data.success) {
        setUsersWithCredits(data.users);
      }
    } catch (error) {
      console.error('Error loading users with credits:', error);
    }
  };

  // Update commission config
  const updateConfig = async (methodology, updates) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/meme-coins/update-commission-config`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            adminUserId: user.id,
            methodology,
            ...updates
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        alert(`✅ ${METHODOLOGY_LABELS[methodology]} config updated successfully!`);
        setEditingMethodology(null);
        await loadConfigs();
      } else {
        alert(`Failed to update config: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating config:', error);
      alert('Error updating config');
    } finally {
      setSaving(false);
    }
  };

  // Process withdrawal (approve/reject/complete)
  const processWithdrawal = async (requestId, action, txSignature = null, rejectionReason = null) => {
    const confirmed = window.confirm(
      `Are you sure you want to ${action} this withdrawal request?`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/meme-coins/process-withdrawal`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            adminUserId: user.id,
            requestId,
            action,
            txSignature,
            rejectionReason
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        alert(`✅ Withdrawal ${action}d successfully!`);
        await loadPendingWithdrawals();
        await loadUsersWithCredits();
      } else {
        alert(`Failed to ${action} withdrawal: ${data.error}`);
      }
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      alert('Error processing withdrawal');
    }
  };

  // Handle withdrawal completion (with tx signature input)
  const handleCompleteWithdrawal = (requestId) => {
    const txSignature = prompt('Enter the transaction signature:');
    
    if (!txSignature) {
      alert('Transaction signature is required to complete withdrawal');
      return;
    }

    processWithdrawal(requestId, 'complete', txSignature);
  };

  // Handle withdrawal rejection
  const handleRejectWithdrawal = (requestId) => {
    const reason = prompt('Enter rejection reason:');
    
    if (!reason) {
      alert('Rejection reason is required');
      return;
    }

    processWithdrawal(requestId, 'reject', null, reason);
  };

  // Check admin access
 

  return (
    <div className="admin-commissions-container">
      <div className="admin-header">
        <h2>⚙️ Commission Management</h2>
        <button onClick={loadAllData} disabled={loading} className="btn-refresh">
          {loading ? '🔄 Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={selectedTab === 'configs' ? 'active' : ''}
          onClick={() => setSelectedTab('configs')}
        >
          📊 Configurations
        </button>
        <button
          className={selectedTab === 'withdrawals' ? 'active' : ''}
          onClick={() => setSelectedTab('withdrawals')}
        >
          💸 Withdrawals ({pendingWithdrawals.length})
        </button>
        <button
          className={selectedTab === 'summary' ? 'active' : ''}
          onClick={() => setSelectedTab('summary')}
        >
          📈 Summary
        </button>
        <button
          className={selectedTab === 'users' ? 'active' : ''}
          onClick={() => setSelectedTab('users')}
        >
          👥 Users with Credits
        </button>
      </div>

      {/* TAB 1: Commission Configurations */}
      {selectedTab === 'configs' && (
        <div className="configs-tab">
          <h3>Commission Configurations by Methodology</h3>
          
          {configs.map(config => (
            <div key={config.methodology} className="config-card">
              <div className="config-header">
                <h4>{METHODOLOGY_LABELS[config.methodology]}</h4>
                {editingMethodology !== config.methodology && (
                  <button
                    onClick={() => setEditingMethodology(config.methodology)}
                    className="btn-edit"
                  >
                    ✏️ Edit
                  </button>
                )}
              </div>

              {editingMethodology === config.methodology ? (
                <CommissionConfigEditor
                  config={config}
                  onSave={updateConfig}
                  onCancel={() => setEditingMethodology(null)}
                  saving={saving}
                  stablecoinOptions={STABLECOIN_OPTIONS}
                  creditTypeOptions={CREDIT_TYPE_OPTIONS}
                />
              ) : (
                <div className="config-display">
                  <div className="config-item">
                    <span className="label">Commission:</span>
                    <span className="value">{config.commission_percent}%</span>
                  </div>
                  <div className="config-item">
                    <span className="label">Credit Type:</span>
                    <span className="value">
                      {CREDIT_TYPE_OPTIONS.find(o => o.value === config.commission_credit_type)?.label}
                      {config.commission_credit_type === 'custom' && ` (${config.custom_credit_percent}%)`}
                    </span>
                  </div>
                  <div className="config-item">
                    <span className="label">Supported Stablecoins:</span>
                    <span className="value">
                      {config.supported_stablecoins?.join(', ') || 'None'}
                    </span>
                  </div>
                  <div className="config-item">
                    <span className="label">Status:</span>
                    <span className={`value status ${config.enabled ? 'enabled' : 'disabled'}`}>
                      {config.enabled ? '✅ Enabled' : '❌ Disabled'}
                    </span>
                  </div>
                  <div className="config-item">
                    <span className="label">Last Updated:</span>
                    <span className="value">{new Date(config.updated_at).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* TAB 2: Pending Withdrawals */}
      {selectedTab === 'withdrawals' && (
        <div className="withdrawals-tab">
          <h3>Pending Credit Withdrawals</h3>
          
          {pendingWithdrawals.length === 0 ? (
            <div className="empty-state">
              ✅ No pending withdrawals
            </div>
          ) : (
            <div className="withdrawals-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Methodology</th>
                    <th>Amount</th>
                    <th>Wallet Address</th>
                    <th>Requested</th>
                    <th>Current Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingWithdrawals.map(withdrawal => (
                    <tr key={withdrawal.id}>
                      <td>{withdrawal.id}</td>
                      <td>
                        <div className="user-info">
                          <div>{withdrawal.email}</div>
                          {withdrawal.discord_id && (
                            <div className="discord-id">Discord: {withdrawal.discord_id}</div>
                          )}
                        </div>
                      </td>
                      <td>{METHODOLOGY_LABELS[withdrawal.methodology]}</td>
                      <td className="amount">${withdrawal.amount_requested.toFixed(2)}</td>
                      <td className="wallet-address">
                        <code>{withdrawal.wallet_address.substring(0, 8)}...{withdrawal.wallet_address.substring(withdrawal.wallet_address.length - 6)}</code>
                      </td>
                      <td>{new Date(withdrawal.requested_at).toLocaleString()}</td>
                      <td className="amount">${(withdrawal.current_balance || 0).toFixed(2)}</td>
                      <td className="actions">
                        <button
                          onClick={() => handleCompleteWithdrawal(withdrawal.id)}
                          className="btn-complete"
                          title="Mark as completed (enter tx signature)"
                        >
                          ✅ Complete
                        </button>
                        <button
                          onClick={() => handleRejectWithdrawal(withdrawal.id)}
                          className="btn-reject"
                          title="Reject and refund credits"
                        >
                          ❌ Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: Commission Summary */}
      {selectedTab === 'summary' && commissionSummary && (
        <div className="summary-tab">
          <h3>Commission Summary</h3>
          
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-label">Total Swaps</div>
              <div className="summary-value">{commissionSummary.total_swaps || 0}</div>
            </div>
            
            <div className="summary-card">
              <div className="summary-label">Total Commission Collected</div>
              <div className="summary-value highlight">
                ${(commissionSummary.total_collected || 0).toFixed(2)}
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-label">Total Credits Given</div>
              <div className="summary-value">
                ${(commissionSummary.total_credited || 0).toFixed(2)}
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-label">Total Credits Applied</div>
              <div className="summary-value">
                ${(commissionSummary.total_credits_applied || 0).toFixed(2)}
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-label">Net Commission (You Keep)</div>
              <div className="summary-value profit">
                ${(commissionSummary.total_net_commission || 0).toFixed(2)}
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-label">Average Commission/Swap</div>
              <div className="summary-value">
                ${(commissionSummary.avg_commission_per_swap || 0).toFixed(2)}
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-label">Total Credits Owed to Users</div>
              <div className="summary-value warning">
                ${(commissionSummary.total_credits_owed || 0).toFixed(2)}
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-label">Pending Withdrawals</div>
              <div className="summary-value">
                {commissionSummary.pending_withdrawals?.count || 0} requests
                <br />
                <small>${(commissionSummary.pending_withdrawals?.amount || 0).toFixed(2)}</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: Users with Credits */}
      {selectedTab === 'users' && (
        <div className="users-tab">
          <h3>Users with Credit Balances</h3>
          
          <div className="users-summary">
            <span>Total Users: {usersWithCredits.length}</span>
            <span>Total Credits Owed: ${usersWithCredits.reduce((sum, u) => sum + u.balance_usdc, 0).toFixed(2)}</span>
          </div>

          {usersWithCredits.length === 0 ? (
            <div className="empty-state">
              No users with credit balances
            </div>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Methodology</th>
                    <th>Balance</th>
                    <th>Total Earned</th>
                    <th>Total Applied</th>
                    <th>Total Withdrawn</th>
                    <th>Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {usersWithCredits.map((user, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="user-info">
                          <div>{user.email}</div>
                          {user.discord_id && (
                            <div className="discord-id">Discord: {user.discord_id}</div>
                          )}
                        </div>
                      </td>
                      <td>{METHODOLOGY_LABELS[user.methodology]}</td>
                      <td className="amount balance">${user.balance_usdc.toFixed(2)}</td>
                      <td className="amount">${user.total_earned.toFixed(2)}</td>
                      <td className="amount">${user.total_applied.toFixed(2)}</td>
                      <td className="amount">${user.total_withdrawn.toFixed(2)}</td>
                      <td>{new Date(user.last_updated).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCommissions;
